import React, { FC, useMemo } from "react";
import { AddCircle, ExpandMore } from "@mui/icons-material";
import AddFormControl from "../addFormControl/AddFormControl";
import { areEqualProps } from "@helperFunctions/checkDidPropChange";
import { IAddFormButtonProps } from "../../../types/formButtons/IAddFormButtonProps";
import { TMenuOption } from "../../../types/contextMenu/TMenuOption";
import { generatePackageOptions } from "../../../types/contextMenu/generatePackageOptions";
import { generateFormOptions } from "../../../types/contextMenu/generateFormOptions.";
import { useGetFormsQuery, useGetPackagesQuery } from "@services/dataFormsApi";
import { EPackageStatus } from "../../../enums/EPackageStatus.enum";

const FormBtn: FC<IAddFormButtonProps> = ({
  handleFormSelection,
  assignedPackageIds,
  handlePackageSelection,
  startIcon,
  startIconColor = "success",
  endIcon,
  buttonText = "Forms",
  width,
  height,
  disabled,
  shouldCloseOnSelect = false,
}): React.JSX.Element => {
  const { data: formsResponse, isLoading: formsLoading } =
    useGetFormsQuery(undefined);
  const { data: packages = [], isLoading: packagesLoading } =
    useGetPackagesQuery(undefined, { refetchOnMountOrArgChange: true });

  const activePackages = useMemo(
    () =>
      packages?.filter(
        (formPackage) => formPackage.status === EPackageStatus.Active
      ) ?? [],
    [packages]
  );

  const packageOptions: TMenuOption[] = generatePackageOptions(
    assignedPackageIds,
    activePackages,
    handlePackageSelection
  );
  const formOptions: TMenuOption[] = generateFormOptions(
    formsResponse?.value ?? [],
    handleFormSelection
  );

  const options = [
    {
      label: "Packages",
      disabled: !packageOptions.length,
      children: packageOptions,
    },
    {
      label: "Forms",
      disabled: !formOptions.length,
      children: formOptions,
    },
  ];
  const StartIcon: React.JSX.Element = useMemo((): React.JSX.Element => {
    if (startIcon) {
      return startIcon;
    }
    if (startIcon === null) {
      return <></>;
    }

    return <AddCircle color={disabled ? "disabled" : startIconColor} />;
  }, [startIcon, startIconColor, disabled]);

  const EndIcon: React.JSX.Element = useMemo((): React.JSX.Element => {
    if (endIcon) {
      return endIcon;
    }
    if (endIcon === null) {
      return <></>;
    }

    return <ExpandMore fontSize="large" />;
  }, [endIcon]);

  return (
    <AddFormControl
      options={options}
      buttonText={buttonText}
      startIcon={StartIcon}
      endIcon={EndIcon}
      width={width}
      height={height}
      disabled={disabled || formsLoading || packagesLoading}
      shouldCloseOnSelect={shouldCloseOnSelect}
    />
  );
};

export const AddFormButton = React.memo(FormBtn, areEqualProps);
